<template>
  <v-layout class="justify-center">

    <v-flex xs9>
      <v-card class="grey lighten-4">
        <v-toolbar color="primary" dark flat dense>
          <v-toolbar-title>Frequently Asked Questions</v-toolbar-title>
        </v-toolbar>
        <v-card-text>

          <v-container>

          <v-expansion-panel accordion focusable :value='panel'>
          <v-expansion-panel-content>
            <div slot="header"><b>General</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>When is my file ready for download?</b><br />
                  Tasks are processed sequentially in the order they are submitted.
                  Depending on the number of queued tasks and the size of the submitted tasks the times until completion will vary.
                  Resubmitting tasks does not lead to faster completion.
                </li>
                <li><b>Will I be notified If my files are ready for download?</b><br />
                  No. Please save your task ID or bookmark the task-ID page and check if your file is ready for download from time to time.
                </li>
                <li><b>How long are my files available for download?</b><br />
                  The results are stored for 14 days and removed thereafter.
                  Please re-submit your task if you need to download the files again.
                </li>
                <li><b>I have a suggestion or need additional help, who can I contact?</b><br />
                  We are happy to receive feedback to prosit.proteomics.wzw [at] tum.de .
                  Please understand that we cannot not troubleshoot all individual task errors.
                  If your tasks result in a reproducible error and you are certain that the files you uploaded comply with the stated instructions and FAQ, we are interested to learn about it.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>

          <v-expansion-panel-content>
            <div slot="header"><b>Troubleshooting</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>I have trouble viewing the website.</b><br />
                  Prosit is optimized for Google Chrome 71.0 and Mozilla Firefox 64.0 on MacOs, Linux and Windows.
                </li>
                <li><b>The file upload is very slow or keeps failing.</b><br />
                  Antivirus software may cancel large uploads or cause timeouts. Consider using an alternative computer, network or antivirus software.
                </li>
                <li><b>My search resulted in an error, what can I do?</b><br />
                  Please make sure you complied with all instructions stated.
                  Please make sure you upload only the correct files with correct file extensions.
                </li>
                <li><b>I have not saved my task-ID, can you tell me which one it is?</b><br />
                  No. Please resubmit your task and save your task ID or bookmark the task-ID page.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>

          <v-expansion-panel-content>
            <div slot="header"><b>Compatibility</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>What peptides and fragments does Prosit support?</b><br />
                  Prosit predicts peptide fragment spectra generated by beam-type collision induced dissociation
                  (beam-type CID; also termed HCD - higher energy collision induced dissociation)
                  for peptides with 7 to 30 amino acid length, precursor charge 1+ to 6+, b-type ions and y-type
                  ions with fragment charge 1+ to 3+. Oxidized methionine can be indicated as “M(Oxidation (M))”.
                  Each C is treated as Cysteine with carbamidomethylation. Prosit does not support J, U or O as amino acids.
                </li>
                <li><b>What mass spectrometer platforms are supported?</b><br />
                  The spectral libraries generated with Prosit are readily applicable to any type of instrument generating
                  HCD spectra and have been tested for compatibility with various Oribtrap and Q-TOF instruments.
                  For collision energy calibration and re-scoring of result files, Prosit supports ThermoFisher
                  Scientific QExactive and Fusion series <code>.raw</code> files containing HCD MS2 spectra only.
                </li>
                <li><b>What retention times does Prosit predict?</b><br />
                  Prosit predicts indexed retention times (iRT) for peptides for spectral library generation tasks.
                  A feature to align the iRT values to actual retention times will be available soon.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>

          <v-expansion-panel-content>
            <div slot="header"><b>Collision energy calibration</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>Why is the calibration of collision energy recommended?</b><br />
                  The fragmentation efficiency varies between instruments and calibration.
                  In order to receive predicted spectra well matching to your instrument we recommend collision energy
                  calibration to determine the optimal collision energy settings for predictions.
                </li>
                <li><b>What file types are supported?</b><br />
                  The CE calibration requires both the <code>msms.txt</code> generated by <a href="https://maxquant.org/" target="_blank">MaxQuant (version 1.5x or 1.6x)</a> and
                  the corresponding ThermoScientific Q Exactive or Fusion series <code>.raw</code> files containing HCD MS2 data.
                  <code>.raw</code> size is limited to 2GB.
                  Please make sure that the naming of the <code>.raw</code> file correctly appears in the respective column in the <code>msms.txt</code> file. Please make sure, the MaxQuant search contained only one raw file.
                </li>
                <li><b>What happens during the calibration process?</b><br />
                  Prosit will select a random subset of high-scoring PSMs and will predict these spectra in different collision energies.
                  Then the CE that achieves highest correlations with the experimental spectra is returned.
                </li>
                <li><b>How to interpret and use the calibration result?</b><br />
                  Prosit will return the collision energy value best matching your instrument data.
                  If you request a spectra library, use this value when asked for a collision energy.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>

          <v-expansion-panel-content>
            <div slot="header"><b>Spectral library prediction</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>What does Prosit provide in terms of spectral libraries?</b><br />
                  Prosit provides ready to use in-silico spectral libraries for any peptide sequence.
                  Peptides are restricted to 7 to 30 amino acid length, precursor charge 1+ to 6+, b-type ions and y-type ions with fragment charge 1+ to 3+.
                  Oxidized methionine can be indicated as “M(Oxidation (M))”. Each C is treated as Cysteine with carbamidomethylation. Prosit does not support J, U or O as amino acids.
                </li>
                <li><b>What is required for library generation?</b><br />
                  Prosit requires a <code>.csv</code> file containing the modified sequence of a peptide, the collision energy and precursor charge.
                  We advise you to run the collision energy calibration to obtain the optimal collision energy value for your instrument.
                  Please make sure the column are spelled correctly: <code>modified_sequence</code>, <code>collision_energy</code> and <code>precursor_charge</code>.
                  Please provide the sequence in upper case letters, oxidized methionine can be indicated as "M(Oxidation (M))".
                  Each C is treated as Cysteine with carbamidomethylation. Prosit does not support J, U or O as amino acids.
                </li>
                <li><b>What file format does the spectral library have?</b><br />
                  You can choose between the <code>.MSP</code> NIST format (which has been tested with Skyline and MSPepSearch) and a generic text format that is compatible with Spectronaut.
                  The generic text format also contains indexed retention time information.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>

          <v-expansion-panel-content>
            <div slot="header"><b>Rescoring of search engine results</b></div>
            <v-card>
              <v-card-text>
                <ul>
                <li><b>What does the rescoring of search engine results do?</b><br />
                  Prosit is not a search engine. By using Prosit, correct and incorrect identifications can, however, be separated more strongly
                  by integrating fragment ion intensity information into the false discovery rate estimation.
                  Using this feature and Percolator for FDR estimation will result in improved, more confident PSMs, especially when employing large databases.
                </li>
                <li><b>What is required for rescoring?</b><br />
                  Prosit requires a <code>.csv</code> file containing the modified sequence of a peptide, the collision energy and precursor charge.
                  We advise you to run the collision energy calibration to obtain the optimal collision energy value for your instrument.
                  Please make sure the column are spelled correctly: <code>modified_sequence</code>, <code>collision_energy</code> and <code>precursor_charge</code>.
                  Please provide the sequence in upper case letters, oxidized methionine can be indicated as “M(Oxidation (M))”.
                  Each C is treated as Cysteine with carbamidomethylation. Prosit does not support J, U or O as amino acids.
                </li>
                <li><b>What is required for rescoring?</b><br />
                  Rescoring requires two files: A <code>msms.txt</code> generated by <a href="https://maxquant.org/" target="_blank">MaxQuant (version 1.5x or 1.6x)</a>  containing HCD MS 2 spectra with PSM and Protein FDR set to 100%, and the
                  the corresponding ThermoScientific Q Exactive or Fusion series <code>.raw</code> file containing HCD MS2 data.
                  Please make sure that the MaxQuant search only contained one <code>.raw</code> file and that the naming of that file correctly appears in the respective column in the <code>msms.txt</code>.
                </li>
                </ul>
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>


          </v-expansion-panel>
          </v-container>





        </v-card-text>

      </v-card>
    </v-flex>
  </v-layout>
</template>


<script>

export default {
  name: 'FAQ',
  methods: {
    newTask: function(){
      this.$router.push('/prosit/');
    }
  },
  data: () => ({
    panel: 0
  }),
}
</script>
